import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Dropdown,
  Input,
  Space,
  Splitter,
  Tooltip,
  notification,
} from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import MpOfferDetailDrawer from '../detail/MpOfferDetailDrawer';
import MpOffersFilters from './MpOffersFilters';
import MpOffersUpdateDrawer from './MpOffersUpdateDrawer';
import MpOffersTable from './MpOffersTable';
import { useExportOffers } from '../../../../utils/api/mpOffers/useExportOffers';
import { useGetMpOffers } from '../../../../utils/api/mpOffers/useGetMpOffers';
import {
  OFFER_EXPORT_FORMATS,
  OFFER_EXPORT_MAX_ROWS,
} from '../../../../utils/const/mpOffers';
import { useOffersFilters } from '../../../../utils/zustand';

const DEFAULT_SIDEBAR_WIDTH = 280;

const MpOfferList = () => {
  const { filters } = useOffersFilters();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [ordering, setOrdering] = useState(null);
  const [sidebarSize, setSidebarSize] = useState(DEFAULT_SIDEBAR_WIDTH);
  const [updateDrawerOpen, setUpdateDrawerOpen] = useState(false);
  const [detailOfferId, setDetailOfferId] = useState(null);
  const [selectedOffers, setSelectedOffers] = useState([]);
  const sidebarOpen = sidebarSize >= 50;

  const [criteria, setCriteria] = useState({ search, filters });
  if (criteria.search !== search || criteria.filters !== filters) {
    setCriteria({ search, filters });
    setCurrentPage(1);
    setSelectedOffers([]);
  }

  const offset = useMemo(
    () => (currentPage - 1) * pageSize,
    [currentPage, pageSize]
  );

  const { mpOffers, count, isLoading, isError, refetch } = useGetMpOffers({
    ...filters,
    search,
    offset,
    limit: pageSize,
    ordering,
  });

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleSortChange = (nextOrdering) => {
    setOrdering(nextOrdering);
    setCurrentPage(1);
  };

  const toggleSidebar = () => {
    setSidebarSize(sidebarOpen ? 0 : DEFAULT_SIDEBAR_WIDTH);
  };

  const handleResize = (newSizes) => {
    setSidebarSize(newSizes[0]);
  };

  const { exportOffers, isLoading: isExporting } = useExportOffers();

  const exportableCount = selectedOffers.length || count || 0;
  const isExportBlocked = exportableCount > OFFER_EXPORT_MAX_ROWS;

  const handleExport = async (format) => {
    const body =
      selectedOffers.length > 0
        ? { offer_ids: selectedOffers.map((offer) => offer.id) }
        : { filters: { ...filters, search } };

    try {
      await exportOffers({ format, async_export: true, ...body });
      notification.success({
        message: 'Export en cours',
        description: "Vous recevrez un email lorsque l'export sera terminé.",
        placement: 'bottomRight',
        duration: 5,
      });
    } catch (error) {
      notification.error({
        message: "Erreur d'export",
        description:
          error?.response?.data?.detail ||
          error?.response?.data?.error ||
          "L'export n'a pas pu être lancé.",
        placement: 'bottomRight',
        duration: 5,
      });
    }
  };

  return (
    <Splitter
      onResize={handleResize}
      style={{ flex: 1, minHeight: 0, height: '100%' }}
    >
      <Splitter.Panel
        size={sidebarSize}
        min={0}
        max={400}
        collapsible
        style={{ overflow: 'auto', transition: 'width 0.2s ease' }}
      >
        <div
          style={{
            padding: sidebarOpen ? '16px' : 0,
            height: '100%',
            backgroundColor: '#fafafa',
            borderRight: '1px solid #f0f0f0',
            display: sidebarOpen ? 'block' : 'none',
          }}
        >
          <MpOffersFilters />
        </div>
      </Splitter.Panel>
      <Splitter.Panel style={{ overflow: 'auto' }}>
        <div style={{ padding: '0 16px' }}>
          <Space align="center" style={{ margin: '8px 0' }}>
            <Button
              type="text"
              icon={sidebarOpen ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
              onClick={toggleSidebar}
              title={sidebarOpen ? 'Fermer le panneau' : 'Ouvrir le panneau'}
            />
            <Input
              size="small"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
              placeholder="Rechercher une offre..."
              style={{ width: 400 }}
              allowClear
            />
          </Space>
          <div
            style={{
              margin: '8px 0',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
            }}
          >
            <Tooltip
              title={
                isExportBlocked
                  ? "Le nombre d'offres exportable est limité à 5000. Veuillez sélectionner des offres spécifiques ou utiliser les filtres."
                  : ''
              }
            >
              <Dropdown
                disabled={isExportBlocked || isExporting}
                menu={{
                  items: OFFER_EXPORT_FORMATS,
                  onClick: ({ key }) => handleExport(key),
                }}
              >
                <Button
                  color="primary"
                  variant="outlined"
                  loading={isExporting}
                  disabled={isExportBlocked}
                >
                  Exporter
                </Button>
              </Dropdown>
            </Tooltip>
            <Button
              color="primary"
              variant="outlined"
              onClick={() => setUpdateDrawerOpen(true)}
            >
              Lancer une mise à jour
            </Button>
          </div>

          {isError && (
            <Alert
              type="error"
              showIcon
              message="Impossible de charger les offres."
              action={
                <Button size="small" onClick={() => refetch()}>
                  Réessayer
                </Button>
              }
              style={{ marginBottom: 8 }}
            />
          )}
          <MpOffersTable
            mpOffers={mpOffers}
            count={count}
            isLoading={isLoading}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            onSortChange={handleSortChange}
            selectedOffers={selectedOffers}
            onSelectionChange={setSelectedOffers}
            onOfferClick={setDetailOfferId}
            country={filters.country ?? null}
          />

          <MpOfferDetailDrawer
            offerId={detailOfferId}
            open={detailOfferId !== null}
            onClose={() => setDetailOfferId(null)}
          />

          <MpOffersUpdateDrawer
            open={updateDrawerOpen}
            onClose={() => setUpdateDrawerOpen(false)}
            selectedOffers={selectedOffers}
            onUpdated={() => setSelectedOffers([])}
          />
        </div>
      </Splitter.Panel>
    </Splitter>
  );
};

export default MpOfferList;
